import React, { useMemo, useState } from 'react';
import { createAssetProcessingDoc, failAssetDoc, finalizeAssetDoc } from './printablesExport';
import { createEmailSignatureHtml, createEmailSignatureText } from './emailSignatureGenerator';
import './EmailSignaturePrintable.css';

const DEFAULT_LOGO_PATH = '/d_logo-removebg-preview.png';
const DEFAULT_PUBLIC_LOGO_URL = 'https://douglas-924ae.web.app/d_logo-removebg-preview.png';

const normalizeText = (value) => {
  if (value === null || value === undefined) return '';
  return String(value).trim();
};

const toPublicHttpUrl = (value) => {
  const normalized = normalizeText(value);
  if (!normalized) return '';
  return /^https?:\/\//i.test(normalized) ? normalized : '';
};

const getAddressLine = (branch) => {
  const address = normalizeText(branch?.address);
  if (address) return address;
  return normalizeText(branch?.city);
};

const getWebsite = (branch, profile) =>
  normalizeText(branch?.website) || normalizeText(profile?.socialLinks?.website);

const getDesignation = (profile, branch) => {
  const designation = normalizeText(profile?.designation);
  if (designation) return designation;
  const city = normalizeText(branch?.city);
  if (city) return `Managing Partner - ${city}`;
  return 'Property Consultant';
};

const copyTextFallback = (text) => {
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'absolute';
  textarea.style.left = '-9999px';
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand('copy');
  document.body.removeChild(textarea);
};

const EmailSignaturePrintable = ({
  firebase,
  authUser,
  tenantId,
  branchId,
  profile,
  branch,
  brandKit,
  assignedEmail,
  assignedEmailMissing,
  compliance,
}) => {
  const [activeTab, setActiveTab] = useState('preview');
  const [saveToAssets, setSaveToAssets] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [warning, setWarning] = useState('');
  const [copyMessage, setCopyMessage] = useState('');

  const addressLine = useMemo(() => getAddressLine(branch), [branch]);
  const website = useMemo(() => getWebsite(branch, profile), [branch, profile]);
  const designation = useMemo(() => getDesignation(profile, branch), [profile, branch]);
  const defaultLogoUrl = useMemo(() => {
    const envLogo = toPublicHttpUrl(process.env.REACT_APP_EMAIL_SIGNATURE_LOGO_URL);
    if (envLogo) return envLogo;
    if (typeof window === 'undefined') return DEFAULT_PUBLIC_LOGO_URL;
    const host = window.location.hostname || '';
    const isLocalHost =
      host === 'localhost' ||
      host === '127.0.0.1' ||
      host === '[::1]' ||
      host.endsWith('.local');
    if (isLocalHost) {
      return DEFAULT_PUBLIC_LOGO_URL;
    }
    const origin = window.location.origin || '';
    return origin ? `${origin}${DEFAULT_LOGO_PATH}` : DEFAULT_PUBLIC_LOGO_URL;
  }, []);
  const signatureLogoUrl = useMemo(
    () => toPublicHttpUrl(brandKit?.logoUrl) || defaultLogoUrl,
    [brandKit?.logoUrl, defaultLogoUrl],
  );

  const [showWhatsApp, setShowWhatsApp] = useState(false);
  const [showAddress, setShowAddress] = useState(true);
  const showWebsite = false;

  const emailValue = normalizeText(assignedEmail) || normalizeText(authUser?.email);
  const missingPhone = compliance.requirePhone && !profile?.phone;
  const missingAddress = compliance.requireAddress && !addressLine;

  const statusTone = missingPhone ? 'error' : assignedEmailMissing || missingAddress ? 'warning' : 'success';
  const statusLabel = missingPhone
    ? 'Missing Phone'
    : missingAddress
      ? 'Missing Address'
      : assignedEmailMissing
        ? 'Assigned Email not set'
        : 'Ready';

  const canSave = Boolean(tenantId && branchId && authUser?.uid);

  const signaturePayload = useMemo(
    () => ({
      brand: {
        logoUrl: signatureLogoUrl,
        colors: brandKit?.colors || {},
      },
      user: {
        fullName: normalizeText(profile?.fullName),
        designation,
        phone: normalizeText(profile?.phone),
        whatsapp: normalizeText(profile?.whatsapp),
        email: emailValue,
      },
      branch: {
        branchName: normalizeText(branch?.branchName),
        addressLine,
        city: normalizeText(branch?.city),
        website,
      },
      options: {
        showWhatsApp,
        showAddress,
        showWebsite,
      },
    }),
    [
      addressLine,
      brandKit?.colors,
      signatureLogoUrl,
      branch?.branchName,
      branch?.city,
      designation,
      emailValue,
      profile?.fullName,
      profile?.phone,
      profile?.whatsapp,
      showAddress,
      showWebsite,
      showWhatsApp,
      website,
    ],
  );

  const htmlSignature = useMemo(
    () => createEmailSignatureHtml(signaturePayload),
    [signaturePayload],
  );

  const plainText = useMemo(
    () => createEmailSignatureText(signaturePayload),
    [signaturePayload],
  );

  const handleCopyHtml = async () => {
    try {
      if (navigator.clipboard && window.ClipboardItem) {
        const htmlBlob = new Blob([htmlSignature], { type: 'text/html' });
        const textBlob = new Blob([plainText], { type: 'text/plain' });
        const item = new window.ClipboardItem({
          'text/html': htmlBlob,
          'text/plain': textBlob,
        });
        await navigator.clipboard.write([item]);
      } else if (navigator.clipboard) {
        await navigator.clipboard.writeText(htmlSignature);
      } else {
        copyTextFallback(htmlSignature);
      }
      setCopyMessage('Copied.');
    } catch (err) {
      console.error('Copy failed:', err);
      setCopyMessage('Copy failed.');
    } finally {
      setTimeout(() => setCopyMessage(''), 1500);
    }
  };

  const handleCopyText = async () => {
    try {
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(plainText);
      } else {
        copyTextFallback(plainText);
      }
      setCopyMessage('Copied.');
    } catch (err) {
      console.error('Copy failed:', err);
      setCopyMessage('Copy failed.');
    } finally {
      setTimeout(() => setCopyMessage(''), 1500);
    }
  };

  const handleDownloadHtml = () => {
    const blob = new Blob([htmlSignature], { type: 'text/html' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'douglas-mccartney-signature.html';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleSave = async () => {
    if (!saveToAssets) return;

    setSaving(true);
    setMessage('');
    setWarning('');
    setError('');

    let assetRef = null;
    try {
      if (!canSave) {
        setWarning('Save failed, asset not stored.');
        return;
      }

      const snapshot = {
        fullName: profile?.fullName || '',
        designation: designation || '',
        phone: profile?.phone || '',
        assignedEmail: emailValue || '',
        branchName: branch?.branchName || '',
        address: addressLine || '',
        city: branch?.city || '',
        reraId: branch?.reraId || '',
        brand: {
          colors: brandKit?.colors || {},
          disclaimerText: brandKit?.disclaimerText || '',
          logoUrl: signatureLogoUrl,
        },
      };

      assetRef = await createAssetProcessingDoc({
        firebase,
        tenantId,
        branchId,
        authUserId: authUser?.uid || '',
        type: 'email_signature',
        snapshot,
      });

      await finalizeAssetDoc({
        firebase,
        assetRef,
        output: {
          dataJson: { html: htmlSignature, text: plainText },
        },
      });

      setMessage('Signature saved to assets.');
    } catch (err) {
      console.error('Signature save failed:', err);
      setError('Could not save signature.');
      if (assetRef) {
        await failAssetDoc({ firebase, assetRef, errorMessage: err.message });
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="printable-layout">
      <div className="printable-preview">
        <div className="email-signature-preview">
          <div className="email-signature-tabs">
            <button
              type="button"
              className={`email-signature-tab ${activeTab === 'preview' ? 'active' : ''}`}
              onClick={() => setActiveTab('preview')}
            >
              Preview
            </button>
            <button
              type="button"
              className={`email-signature-tab ${activeTab === 'install' ? 'active' : ''}`}
              onClick={() => setActiveTab('install')}
            >
              Install
            </button>
          </div>
          {activeTab === 'preview' ? (
            <div className="email-signature-frame">
              <div dangerouslySetInnerHTML={{ __html: htmlSignature }} />
            </div>
          ) : (
            <div className="email-signature-install">
              <div className="email-signature-install-section">
                <h4>Gmail</h4>
                <ol>
                  <li>Open Settings and select See all settings.</li>
                  <li>Under General, scroll to Signature.</li>
                  <li>Create a new signature and click inside the box.</li>
                  <li>Click Copy HTML, then paste into the signature box.</li>
                  <li>Scroll down and save changes.</li>
                </ol>
              </div>
              <div className="email-signature-install-section">
                <h4>Outlook</h4>
                <ol>
                  <li>Open Signature settings and create a new signature.</li>
                  <li>Download the HTML file and open it.</li>
                  <li>Copy the contents and paste into the signature editor.</li>
                  <li>Save the signature and set it as default.</li>
                </ol>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="printable-panel">
        <div className={`printable-status ${statusTone}`}>{statusLabel}</div>
        {assignedEmailMissing && (
          <div className="printable-banner warning">Assigned email not set yet.</div>
        )}
        {missingPhone && (
          <div className="printable-banner error">
            Phone required.
            <button
              type="button"
              className="printable-link"
              onClick={() => window.location.assign('/account')}
            >
              Fix in Profile
            </button>
          </div>
        )}
        {missingAddress && (
          <div className="printable-banner warning">
            Address missing.
            <button
              type="button"
              className="printable-link"
              onClick={() => window.location.assign('/account')}
            >
              Fix in Profile
            </button>
          </div>
        )}
        {message && <div className="printable-banner info">{message}</div>}
        {warning && <div className="printable-banner warning">{warning}</div>}
        {error && <div className="printable-banner error">{error}</div>}
        {copyMessage && <div className="printable-banner info">{copyMessage}</div>}

        <div className="email-signature-section">
          <h4>Fields Used</h4>
          <div className="printable-detail">
            <span>Name</span>
            <strong>{profile.fullName || 'Not set'}</strong>
          </div>
          <div className="printable-detail">
            <span>Designation</span>
            <strong>{designation || 'Not set'}</strong>
          </div>
          <div className="printable-detail">
            <span>Phone</span>
            <strong>{profile.phone || 'Not set'}</strong>
          </div>
          <div className="printable-detail">
            <span>Email</span>
            <strong>{emailValue || 'Not set'}</strong>
          </div>
          <div className="printable-detail">
            <span>Address</span>
            <strong>{addressLine || 'Not set'}</strong>
          </div>
        </div>

        <div className="email-signature-section">
          <h4>Options</h4>
          <label className="printable-checkbox">
            <input
              type="checkbox"
              checked={showWhatsApp}
              onChange={(event) => setShowWhatsApp(event.target.checked)}
            />
            Show WhatsApp
          </label>
          <label className="printable-checkbox">
            <input
              type="checkbox"
              checked={showAddress}
              onChange={(event) => setShowAddress(event.target.checked)}
            />
            Show Address
          </label>
        </div>

        <div className="printable-actions">
          <div className="printable-actions-row">
            <button type="button" className="printable-action" onClick={handleCopyHtml}>
              Copy HTML
            </button>
            <button type="button" className="printable-action" onClick={handleCopyText}>
              Copy Text
            </button>
            <button type="button" className="printable-action" onClick={handleDownloadHtml}>
              Download HTML File
            </button>
          </div>
          <label className="printable-checkbox">
            <input
              type="checkbox"
              checked={saveToAssets}
              onChange={(event) => setSaveToAssets(event.target.checked)}
            />
            Save to Assets Library
          </label>
          <button
            type="button"
            className="printable-action"
            disabled={saving || !saveToAssets}
            onClick={handleSave}
          >
            {saving ? 'Saving...' : 'Save to Assets'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmailSignaturePrintable;
